import pick from 'lodash/pick';

import { FacebookAddPaymentInfoEvent } from '../analytics/facebook';
import { FirebaseAddPaymentInfoEvent } from '../analytics/firebase';
import { stripeAddPaymentMethod, stripeCreateCustomer, stripeDeletePaymentMethod } from '../util/api';
import { denormalisedResponseEntities } from '../util/data';
import { storableError } from '../util/errors';
import { logError } from '../util/log';
import { currentUserShowSuccess, fetchCurrentUser } from './user.duck';

// ================ Action types ================ //

export const SET_INITIAL_VALUES = 'app/paymentMethods/SET_INITIAL_VALUES';

export const CREATE_STRIPE_CUSTOMER_REQUEST = 'app/paymentMethods/CREATE_STRIPE_CUSTOMER_REQUEST';
export const CREATE_STRIPE_CUSTOMER_SUCCESS = 'app/paymentMethods/CREATE_STRIPE_CUSTOMER_SUCCESS';
export const CREATE_STRIPE_CUSTOMER_ERROR = 'app/paymentMethods/CREATE_STRIPE_CUSTOMER_ERROR';

export const ADD_PAYMENT_METHOD_REQUEST = 'app/paymentMethods/ADD_PAYMENT_METHOD_REQUEST';
export const ADD_PAYMENT_METHOD_SUCCESS = 'app/paymentMethods/ADD_PAYMENT_METHOD_SUCCESS';
export const ADD_PAYMENT_METHOD_ERROR = 'app/paymentMethods/ADD_PAYMENT_METHOD_ERROR';

export const DELETE_PAYMENT_METHOD_REQUEST = 'app/paymentMethods/DELETE_PAYMENT_METHOD_REQUEST';
export const DELETE_PAYMENT_METHOD_SUCCESS = 'app/paymentMethods/DELETE_PAYMENT_METHOD_SUCCESS';
export const DELETE_PAYMENT_METHOD_ERROR = 'app/paymentMethods/DELETE_PAYMENT_METHOD_ERROR';

// ================ Reducer ================ //

/** @type {import('../../types/vansite').VanSite.State.PaymentMethods} */
const initialState = {
  addPaymentMethodInProgress: false,
  addPaymentMethodError: null,
  deletePaymentMethodInProgress: false,
  deletePaymentMethodError: null,
  createStripeCustomerInProgress: false,
  createStripeCustomerError: null,
};

export default function payoutMethodsPageReducer(state = initialState, action = {}) {
  const { type, payload } = action;
  switch (type) {
    case SET_INITIAL_VALUES:
      return { ...initialState, ...payload };
    case CREATE_STRIPE_CUSTOMER_REQUEST:
      return { ...state, createStripeCustomerError: null, createStripeCustomerInProgress: true };
    case CREATE_STRIPE_CUSTOMER_SUCCESS:
      return {
        ...state,
        createStripeCustomerInProgress: false,
        stripeCustomer: payload,
      };
    case CREATE_STRIPE_CUSTOMER_ERROR:
      return {
        ...state,
        createStripeCustomerError: payload,
        createStripeCustomerInProgress: false,
      };
    case ADD_PAYMENT_METHOD_REQUEST:
      return { ...state, addPaymentMethodError: null, addPaymentMethodInProgress: true };
    case ADD_PAYMENT_METHOD_SUCCESS:
      return {
        ...state,
        addPaymentMethodInProgress: false,
        stripeCustomer: payload,
      };
    case ADD_PAYMENT_METHOD_ERROR:
      return {
        ...state,
        addPaymentMethodError: payload,
        addPaymentMethodInProgress: false,
      };

    case DELETE_PAYMENT_METHOD_REQUEST:
      return { ...state, deletePaymentMethodError: null, deletePaymentMethodInProgress: true };
    case DELETE_PAYMENT_METHOD_SUCCESS:
      return {
        ...state,
        deletePaymentMethodInProgress: false,
      };
    case DELETE_PAYMENT_METHOD_ERROR:
      return {
        ...state,
        deletePaymentMethodError: payload,
        deletePaymentMethodInProgress: false,
      };
    default:
      return state;
  }
}

// ================ Action creators ================ //

export const setInitialValues = (initialValues) => ({
  type: SET_INITIAL_VALUES,
  payload: pick(initialValues, Object.keys(initialState)),
});

export const stripeCustomerCreateRequest = () => ({ type: CREATE_STRIPE_CUSTOMER_REQUEST });

export const stripeCustomerCreateSuccess = (stripeCustomer) => ({
  type: CREATE_STRIPE_CUSTOMER_SUCCESS,
  payload: stripeCustomer,
});

export const stripeCustomerCreateError = (e) => ({
  type: CREATE_STRIPE_CUSTOMER_ERROR,
  payload: e,
  error: true,
});

export const addPaymentMethodRequest = () => ({ type: ADD_PAYMENT_METHOD_REQUEST });

export const addPaymentMethodSuccess = (stripeCustomer) => ({
  type: ADD_PAYMENT_METHOD_SUCCESS,
  payload: stripeCustomer,
});

export const addPaymentMethodError = (e) => ({
  type: ADD_PAYMENT_METHOD_ERROR,
  payload: e,
  error: true,
});

export const deletePaymentMethodRequest = () => ({ type: DELETE_PAYMENT_METHOD_REQUEST });

export const deletePaymentMethodSuccess = (stripeCustomer) => ({
  type: DELETE_PAYMENT_METHOD_SUCCESS,
  payload: stripeCustomer,
});

export const deletePaymentMethodError = (e) => ({
  type: DELETE_PAYMENT_METHOD_ERROR,
  payload: e,
  error: true,
});

// ================ Thunks ================ //

export const createStripeCustomer = (stripePaymentMethodId) => (dispatch, getState, sdk) => {
  dispatch(stripeCustomerCreateRequest());
  return sdk.currentUser
    .show()
    .then((res) => {
      const user = res.data.data;
      return stripeCreateCustomer({ stripePaymentMethodId, user });
    })
    .then((response) => {
      const { stripeCustomer, paymentMethod } = response.data;
      return sdk.currentUser.updateProfile({
        privateData: {
          stripeCustomer,
          paymentMethod,
        },
      });
    })
    .then((user) => {
      dispatch(stripeCustomerCreateSuccess(user));
      return user;
    })
    .catch((e) => {
      logError(storableError(e), 'create-stripe-user-failed');
      dispatch(stripeCustomerCreateError(storableError(e)));
    });
};

export const addPaymentMethod = (stripePaymentMethodId, stripeCustomerId) => (dispatch, getState, sdk) => {
  dispatch(addPaymentMethodRequest());
  return stripeAddPaymentMethod({ stripePaymentMethodId, stripeCustomerId })
    .then((response) => {
      const { stripeCustomer, paymentMethod, mandate } = response.data;
      return sdk.currentUser.updateProfile({
        privateData: {
          stripeCustomer,
          paymentMethod,
          mandate,
        },
      });
    })
    .then((user) => {
      dispatch(addPaymentMethodSuccess(user));
      return user;
    })
    .catch((e) => {
      logError(storableError(e), 'add-payment-method-failed');
      dispatch(addPaymentMethodError(storableError(e)));
    });
};

export const deletePaymentMethod = () => async (dispatch, getState, sdk) => {
  dispatch(deletePaymentMethodRequest());

  try {
    const currentUserResponse = await sdk.currentUser.show({ include: ['stripeCustomer'] });
    let currentUser = denormalisedResponseEntities(currentUserResponse)[0];
    const isVansiteStripeCustomer = currentUser.attributes.profile.privateData.stripeCustomer;
    const hasVansitePaymentMethod = currentUser.attributes.profile.privateData.paymentMethod;

    // At some point we implemented stripe customer and stripe payment method
    // to be in the private data to have full control over it
    // and not use the sharetribe implementation
    if (!(isVansiteStripeCustomer && hasVansitePaymentMethod)) {
      const response = await sdk.stripeCustomer.deletePaymentMethod({}, { expand: true });
      currentUser = response.data.data;
    }
    const { privateData } = currentUser.attributes.profile;
    const stripeCustomerId =
      privateData.stripeCustomer?.id || currentUser.stripeCustomer?.attributes?.stripeCustomerId;

    if (!stripeCustomerId) {
      dispatch(deletePaymentMethodSuccess(currentUser));
      return currentUser;
    }
    const response = await stripeDeletePaymentMethod({ stripeCustomerId });
    const { stripeCustomer } = response.data;
    const updateProfileResponse = await sdk.currentUser.updateProfile(
      {
        privateData: {
          stripeCustomer,
          paymentMethod: null,
          mandate: null,
        },
      },
      { expand: true },
    );
    currentUser = updateProfileResponse.data.data;
    dispatch(deletePaymentMethodSuccess(currentUser));
    dispatch(currentUserShowSuccess(currentUser));
    return currentUser;
  } catch (e) {
    logError(storableError(e), 'delete-payment-method-failed');
    dispatch(deletePaymentMethodError(storableError(e)));
  }
};

export const updatePaymentMethod = (stripePaymentMethodId, customerId) => (dispatch) => {
  return dispatch(deletePaymentMethod())
    .then(() => {
      return dispatch(addPaymentMethod(stripePaymentMethodId, customerId));
    })
    .catch((e) => {
      logError(storableError(e), 'updating-payment-method-failed');
    });
};

// This function helps to choose correct thunk function
export const savePaymentMethod = (setupIntent) => async (dispatch, getState) => {
  const currentUser = getState().user.currentUser;

  const stripeCustomer = currentUser.attributes.profile.privateData.stripeCustomer;

  const paymentMethodId = setupIntent.payment_method;

  const hasAlreadyDefaultPaymentMethod = stripeCustomer?.invoice_settings?.default_payment_method;

  try {
    const savePromise = !stripeCustomer
      ? dispatch(createStripeCustomer(paymentMethodId))
      : hasAlreadyDefaultPaymentMethod
        ? dispatch(updatePaymentMethod(paymentMethodId, stripeCustomer.id))
        : dispatch(addPaymentMethod(paymentMethodId, stripeCustomer.id));

    const response = await savePromise;

    const { createStripeCustomerError, addPaymentMethodError, deletePaymentMethodError } =
      getState().paymentMethods;

    if (createStripeCustomerError || addPaymentMethodError || deletePaymentMethodError) {
      return {
        errors: { createStripeCustomerError, addPaymentMethodError, deletePaymentMethodError },
      };
    }

    dispatch(fetchCurrentUser());
    const paymentMethodType = setupIntent.payment_method_types[0];
    FirebaseAddPaymentInfoEvent.withPaymentInfo(paymentMethodType).logEvent();
    FacebookAddPaymentInfoEvent.withCurrentUserUUID(currentUser.id.uuid).logEvent();
    return response;
  } catch (e) {
    console.error(e);
  }
};
